import { useSearchParams } from 'react-router-dom';
import { useCallback, useRef, useEffect } from 'react';

export interface URLParamsConfig {
  initialValues?: Record<string, string | number | boolean>;
  paramTypes?: Record<string, 'string' | 'number' | 'boolean'>;
}

export function useURLParams(config: URLParamsConfig = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialized = useRef(false);
  const { initialValues = {}, paramTypes = {} } = config;

  // Helper to parse parameter value based on type
  const parseParam = useCallback((key: string, value: string) => {
    const type = paramTypes[key] || 'string';
    
    switch (type) {
      case 'number':
        const num = parseInt(value);
        return isNaN(num) ? null : num;
      case 'boolean':
        return value === 'true';
      default:
        return value;
    }
  }, [paramTypes]);

  // Get current parameter values
  const getParams = useCallback(() => {
    const params: Record<string, any> = {};
    
    // Process parameters defined in initialValues
    for (const [key, defaultValue] of Object.entries(initialValues)) {
      const urlValue = searchParams.get(key);
      if (urlValue !== null) {
        params[key] = parseParam(key, urlValue);
      } else {
        params[key] = defaultValue;
      }
    }
    
    // Process parameters defined in paramTypes but not in initialValues
    for (const key of Object.keys(paramTypes)) {
      if (!(key in params)) {
        const urlValue = searchParams.get(key);
        if (urlValue !== null) {
          params[key] = parseParam(key, urlValue);
        } else {
          params[key] = null;
        }
      }
    }
    
    return params;
  }, [searchParams, initialValues, paramTypes, parseParam]);

  // Update URL parameters while preserving others
  const updateParams = useCallback((newParams: Record<string, string | number | boolean | null>) => {
    const currentParams = new URLSearchParams(searchParams.toString());
    
    Object.entries(newParams).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined) {
        currentParams.delete(key);
      } else {
        currentParams.set(key, value.toString());
      }
    });
    
    setSearchParams(currentParams);
  }, [searchParams, setSearchParams]);

  // Initialize from localStorage if no URL params (for auth redirects)
  const initializeFromStorage = useCallback((storageKey: string, paramKey: string) => {
    if (isInitialized.current) return;
    
    const urlValue = searchParams.get(paramKey);
    if (!urlValue) {
      const storedValue = localStorage.getItem(storageKey);
      if (storedValue) {
        updateParams({ [paramKey]: storedValue });
        localStorage.removeItem(storageKey);
      }
    }
  }, [searchParams, updateParams]);

  // Mark as initialized after first render
  useEffect(() => {
    isInitialized.current = true;
  }, []);

  return {
    params: getParams(),
    updateParams,
    initializeFromStorage,
    isInitialized: isInitialized.current
  };
}